import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, fmtErr } from "../lib/api";
import { useAuth } from "../lib/auth";

export const TeamPanel = () => {
  const { user: me } = useAuth();
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("member");
  const [busy, setBusy] = useState(false);

  const load = () => api.teamList().then(setData).catch(() => {});
  useEffect(() => { load(); }, []);

  const invite = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.teamInvite({ email, name, role });
      if (r.email_sent) {
        toast.success(`Invitation emailed to ${email}`);
      } else {
        await navigator.clipboard.writeText(r.invite_link).catch(() => {});
        toast.warning("Email could not be sent — invite link copied to clipboard instead", { duration: 8000 });
      }
      setEmail(""); setName(""); setRole("member");
      load();
    } catch (e2) {
      toast.error(fmtErr(e2));
    } finally {
      setBusy(false);
    }
  };

  const setActive = async (u, active) => {
    try {
      await api.teamPatch(u.id, { active });
      toast.success(active ? `${u.email} reactivated` : `${u.email} deactivated — they can no longer sign in`);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const reset2fa = async (u) => {
    if (!window.confirm(`Reset 2FA for ${u.email}? They'll re-scan a new QR code at their next login.`)) return;
    try {
      await api.teamReset2fa(u.id);
      toast.success("2FA reset — they'll set it up again on next login");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const resend = async (inv) => {
    try {
      const r = await api.inviteResend(inv.id);
      if (r.email_sent) toast.success(`Invitation re-sent to ${inv.email}`);
      else {
        await navigator.clipboard.writeText(r.invite_link).catch(() => {});
        toast.warning("Email could not be sent — fresh invite link copied to clipboard", { duration: 8000 });
      }
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const revoke = async (inv) => {
    try {
      await api.inviteRevoke(inv.id);
      toast.success("Invitation revoked");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  if (!data) return null;

  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-5 space-y-4" data-testid="team-panel">
      <div>
        <h3 className="font-heading text-base font-medium">Team</h3>
        <p className="text-xs text-zinc-500 mt-0.5">Invite colleagues by email. Everyone signs in with a password + authenticator code on every login. Members can use everything except this Team section.</p>
      </div>

      <form onSubmit={invite} className="flex flex-wrap items-end gap-2">
        <label className="flex-1 min-w-[180px]">
          <span className="block text-[11px] font-medium text-zinc-600 uppercase tracking-wider mb-1">Email</span>
          <input data-testid="team-invite-email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="colleague@medicysltd.co.uk" className="ch-input" />
        </label>
        <label className="w-40">
          <span className="block text-[11px] font-medium text-zinc-600 uppercase tracking-wider mb-1">Name (optional)</span>
          <input data-testid="team-invite-name" value={name} onChange={e => setName(e.target.value)} className="ch-input" />
        </label>
        <label className="w-32">
          <span className="block text-[11px] font-medium text-zinc-600 uppercase tracking-wider mb-1">Role</span>
          <select data-testid="team-invite-role" value={role} onChange={e => setRole(e.target.value)} className="ch-input">
            <option value="member">Member</option>
            <option value="admin">Admin</option>
          </select>
        </label>
        <button data-testid="team-invite-submit" type="submit" disabled={busy || !email} className="ch-btn-primary h-9 disabled:opacity-40">
          {busy ? "Sending…" : "Send invite"}
        </button>
      </form>

      {data.invites.length > 0 && (
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 mb-1.5">Pending invitations</div>
          <div className="divide-y divide-zinc-100 border border-zinc-100 rounded-md">
            {data.invites.map(inv => (
              <div key={inv.id} className="flex items-center justify-between gap-3 px-3 py-2" data-testid={`invite-row-${inv.email}`}>
                <div className="min-w-0">
                  <div className="text-sm text-zinc-900 truncate">{inv.email}</div>
                  <div className="text-[11px] text-zinc-500">
                    {inv.role} · {inv.expired ? <span className="text-rose-600 font-medium">expired</span> : `expires ${new Date(inv.expires_at).toLocaleString()}`}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button data-testid={`invite-copy-${inv.email}`} onClick={async () => { await navigator.clipboard.writeText(inv.link).catch(() => {}); toast.success("Invite link copied"); }} className="text-xs px-2 py-1 rounded border border-zinc-200 hover:bg-zinc-50">Copy link</button>
                  <button data-testid={`invite-resend-${inv.email}`} onClick={() => resend(inv)} className="text-xs px-2 py-1 rounded border border-zinc-200 hover:bg-zinc-50">Resend</button>
                  <button data-testid={`invite-revoke-${inv.email}`} onClick={() => revoke(inv)} className="text-xs px-2 py-1 rounded border border-rose-200 text-rose-600 hover:bg-rose-50">Revoke</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 mb-1.5">Members</div>
        <div className="divide-y divide-zinc-100 border border-zinc-100 rounded-md">
          {data.users.map(u => (
            <div key={u.id} className="flex items-center justify-between gap-3 px-3 py-2" data-testid={`member-row-${u.email}`}>
              <div className="min-w-0">
                <div className="text-sm text-zinc-900 truncate">
                  {u.name || u.email}
                  {u.id === me?.id && <span className="text-[11px] text-zinc-400 ml-1.5">(you)</span>}
                </div>
                <div className="text-[11px] text-zinc-500 truncate">{u.email} · last login {u.last_login_at ? new Date(u.last_login_at).toLocaleString() : "never"}</div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border ${u.role === "admin" ? "border-indigo-200 bg-indigo-50 text-indigo-700" : "border-zinc-200 bg-zinc-50 text-zinc-600"}`}>{u.role}</span>
                <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border ${u.is_totp_enabled ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-amber-200 bg-amber-50 text-amber-700"}`}>{u.is_totp_enabled ? "2FA on" : "2FA pending"}</span>
                {!u.active && <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border border-rose-200 bg-rose-50 text-rose-700">deactivated</span>}
                {u.id !== me?.id && (
                  <>
                    {u.is_totp_enabled && <button data-testid={`member-reset2fa-${u.email}`} onClick={() => reset2fa(u)} className="text-xs px-2 py-1 rounded border border-zinc-200 hover:bg-zinc-50">Reset 2FA</button>}
                    {u.active
                      ? <button data-testid={`member-deactivate-${u.email}`} onClick={() => setActive(u, false)} className="text-xs px-2 py-1 rounded border border-rose-200 text-rose-600 hover:bg-rose-50">Deactivate</button>
                      : <button data-testid={`member-reactivate-${u.email}`} onClick={() => setActive(u, true)} className="text-xs px-2 py-1 rounded border border-emerald-200 text-emerald-700 hover:bg-emerald-50">Reactivate</button>}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
